/**
 * L2 launcher control — ToolGrid.
 *
 * A grid of quick-action LAUNCHER tiles: an icon over a label that FIRES an
 * action. It is not OptionSelect drawn differently — the difference is what
 * survives the tap. A picker HOLDS a selection (the chip stays lit, the page
 * reads it back through `applyFilter`/`setValue`); a launcher holds nothing,
 * because "scan this as a spreadsheet" is a door, not a state. So there is no
 * `useSelection`, no `selected`, no `setValue` slot and no `changed`: one tile,
 * one action, and nothing lit afterwards.
 *
 * `activated` carries `{key}` and SPREADS the tile's own `payload` at the top
 * level (keeping it nested under `payload` too) — the same shape OptionSelect
 * spreads an option's payload with, and for the same reason: a connection
 * delivers the payload as the slot's ARGUMENT, so a tile whose recipe stayed
 * nested would reach `host.openCapture {kind,title,prompt}` with no prompt at
 * all. That is what lets a toolbox of eight seeded captures ride ONE
 * connection instead of eight, with the tile — not the page — declaring what
 * it means.
 *
 * The drawing is the Scanner Deck's: a row-wrapping grid (4 columns by
 * default) of circular tinted icon discs over short labels, with an optional
 * corner `badge` on the tile that wants to be noticed. `tone` is per tile with
 * a component-wide default, so a deck is one accent unless a tile says
 * otherwise. Every colour is a token — the disc is the tone's SOFT plate and
 * the glyph the tone itself — so the deck reads in both themes.
 */
import { useState } from 'react';
import { TYPE_STYLE, resetButton, hitTarget, toneColor, toneSoft } from './ui';
import { Icon } from './primitives';

export function ToolGrid({ props = {}, emit }) {
  const tiles = Array.isArray(props.tiles) ? props.tiles.filter(Boolean) : [];
  const columns = Number(props.columns) || 4;
  // the PRESSED state the registry declares, held for exactly as long as the
  // thumb is down: a launcher may light while it is being pressed, and must
  // never still be lit once it has fired.
  const [pressed, setPressed] = useState(null);

  if (!tiles.length) return null;

  const fire = (tile) => {
    const out = { key: tile.key };
    // The tile's own payload rides at the TOP LEVEL of the signal (and stays
    // nested under `payload`), exactly as an option's does in OptionSelect —
    // a connection hands the payload to the slot as its argument, so nested
    // is the same as absent for a typed host slot.
    if (tile.payload !== undefined) {
      out.payload = tile.payload ?? null;
      if (tile.payload && typeof tile.payload === 'object' && !Array.isArray(tile.payload)) {
        Object.assign(out, tile.payload);
      }
    }
    emit?.('activated', out);
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`,
      gap: 'var(--sp-3)',
    }}>
      {tiles.map((tile) => {
        const tone = tile.tone || props.tone || 'accent';
        const label = tile.label || tile.key;
        const down = pressed === tile.key;
        return (
          <button
            key={tile.key}
            type="button"
            aria-label={label}
            onClick={() => fire(tile)}
            onPointerDown={() => setPressed(tile.key)}
            onPointerUp={() => setPressed(null)}
            onPointerLeave={() => setPressed(null)}
            onPointerCancel={() => setPressed(null)}
            style={{
              ...resetButton,
              ...hitTarget,
              minWidth: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              padding: 'var(--sp-1) 0',
              opacity: down ? 0.72 : 1,
              transform: down ? 'scale(0.96)' : 'none',
              transition: 'transform 120ms var(--ease), opacity 120ms var(--ease)',
            }}
          >
            <span style={{ position: 'relative', flex: '0 0 auto' }}>
              <span style={{
                width: 48, height: 48, borderRadius: '50%',
                background: toneSoft(tone),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon props={{ name: tile.icon, size: 20, tone }} />
              </span>
              {/* the corner FLAG — one reading, drawn the way the kit draws
                  every chip (a tone's soft plate + that tone's ink), so a
                  deck's "HOT" is the same object as a card's badge and not a
                  second vocabulary for it. */}
              {tile.badge ? (
                <span style={{
                  ...TYPE_STYLE.eyebrow,
                  position: 'absolute', top: -2, right: -6,
                  padding: '1px 6px', borderRadius: 'var(--r-pill)',
                  background: toneSoft('bad'), color: toneColor('bad'),
                  fontSize: 9, letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}>{tile.badge}</span>
              ) : null}
            </span>
            <span style={{
              ...TYPE_STYLE.caption,
              fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--text-2)',
              textAlign: 'center', lineHeight: 1.25,
              minWidth: 0, width: '100%',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
